import { useEffect, useCallback, useState, useRef, useMemo } from 'react'
import { chakra, Button, HStack, VStack, Box, IconButton, Tooltip, Text } from '@chakra-ui/react'
import { useStores } from 'hooks/useStores'
import get from 'lodash/get'
import set from 'lodash/set'
import { observer } from 'mobx-react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { Col } from 'reactstrap'
import SvgIcon from 'components/SvgIcon'
import { IStepWithRelations } from 'interfaces/step'
import { getValidArray } from 'utils/common'
import { toast } from 'react-toastify'
import StepCard from './components/StepCard'
import ReviewBanner from './components/ReviewBanner'
import ReviewValidationModal from './components/ReviewValidationModal'
import ReviewSummaryModal from './components/ReviewSummaryModal'
import SignOffConfirmationModal from './components/SignOffConfirmationModal'
import styles from './planStationDetail.module.scss'
import { ReviewOption } from 'constants/workplan'

const PlanStationDetail = () => {
  const params = useParams()
  const location = useLocation()
  const { planStore, organizationStore } = useStores()

  const stationId = Number(get(params, 'id', ''))
  const steps: IStepWithRelations[] = (planStore?.stationDetail?.steps || []) as IStepWithRelations[]

  const navigate = useNavigate()

  const [showToc, setShowToc] = useState<boolean>(false)
  const [isReviewValidationModalOpen, setIsReviewValidationModalOpen] = useState<boolean>(false)
  const [isReviewSummaryModalOpen, setIsReviewSummaryModalOpen] = useState<boolean>(false)
  const [isSignOffModalOpen, setIsSignOffModalOpen] = useState<boolean>(false)
  const [reviewedItems, setReviewedItems] = useState<Set<string>>(new Set())
  const primaryColor = organizationStore?.organization?.theme?.primaryColor || '#3182CE'

  const headerRef = useRef<HTMLDivElement | null>(null)

  // TODO: Remove this when BE is ready now it use for demo purpose
  const planId = useMemo(() => {
    const searchParams = new URLSearchParams(location.search)
    const planIdParam = searchParams.get('planId')
    return planIdParam ? Number(planIdParam) : null
  }, [location.search])

  const isReviewMode = false
  const [isReviewModeActive, setIsReviewModeActive] = useState<boolean>(isReviewMode)

  useEffect(() => {
    setIsReviewModeActive(isReviewMode)
    setReviewedItems(new Set())
    setIsReviewSummaryModalOpen(false)
    setIsSignOffModalOpen(false)
  }, [isReviewMode])

  // Generate dumb data for items that need review
  const itemsToReview = useMemo(() => {
    if (!isReviewModeActive || !steps || steps.length === 0) return []
    
    const items: string[] = []
    
    // Add all blocks in first step (or at least first 3 blocks)
    const firstStep = steps[0]
    const firstStepId = firstStep?._id || firstStep?.id
    if (firstStepId && firstStep?.textContents && firstStep.textContents.length > 0) {
      const blocksToMark = firstStep.textContents.slice(0, 3) // Mark first 3 blocks
      blocksToMark.forEach((textContent: any) => {
        const blockId = textContent?.id || textContent?._id
        if (blockId) {
          items.push(`step-${firstStepId}-block-${blockId}`)
        }
      })
    }
   
    // Also mark blocks in second step if exists (first 2 blocks)
    if (steps.length > 1) {
      const secondStep = steps[1]
      const secondStepId = secondStep?._id || secondStep?.id
      if (secondStepId && secondStep?.textContents && secondStep.textContents.length > 0) {
        const secondStepBlocks = secondStep.textContents.slice(0, 2)
        secondStepBlocks.forEach((textContent: any) => {
          const blockId = textContent?.id || textContent?._id
          if (blockId) {
            items.push(`step-${secondStepId}-block-${blockId}`)
          }
        })
      }
    }
    
    return items
  }, [isReviewModeActive, steps])

  // Create review status map for all items (better for BE integration)
  const reviewStatusMap = useMemo(() => {
    if (!isReviewModeActive) return {}
    const map: Record<string, { needsReview: boolean; isReviewed: boolean }> = {}
    itemsToReview.forEach(itemId => {
      map[itemId] = {
        needsReview: true,
        isReviewed: reviewedItems.has(itemId)
      }
    })
    return map
  }, [isReviewModeActive, itemsToReview, reviewedItems])

  const isAllReviewed = useMemo(() => {
    if (!isReviewModeActive || itemsToReview.length === 0) return true
    return itemsToReview.every(item => reviewedItems.has(item))
  }, [isReviewModeActive, itemsToReview, reviewedItems])

  const handleToggleReviewItem = (itemId: string) => {
    setReviewedItems(prev => {
      const newSet = new Set(prev)
      if (newSet.has(itemId)) {
        newSet.delete(itemId)
      } else {
        newSet.add(itemId)
      }
      return newSet
    })
  }

  const handleMarkAsReviewed = () => {
    if (!isAllReviewed) {
      setIsReviewValidationModalOpen(true)
      return
    }
    if (requiresSignOff) {
      setIsSignOffModalOpen(true)
      return
    }
    // TODO: Call API when BE is ready
    toast.success('Changes reviewed successfully')
    setReviewedItems(new Set())
    setIsReviewModeActive(false)
    setIsReviewSummaryModalOpen(false)
  }

  const handleConfirmSignOff = (_credentials: { username: string; password: string }) => {
    // TODO: Call API when BE is ready
    toast.success('Sign off completed')
    setReviewedItems(new Set())
    setIsReviewModeActive(false)
    setIsReviewSummaryModalOpen(false)
    setIsSignOffModalOpen(false)
  }

  const handleViewSummary = () => {
    if (!isReviewModeActive) return
    setIsReviewSummaryModalOpen(true)
  }

  const fetchData = useCallback(async () => {
    if (!stationId) return
    try {
      await planStore.fetchStationDetail(Number(stationId))
    } catch (error: any) {
      console.error('Error fetching plan data:', error)
      if (error?.response?.status === 403) {
        toast.error('You are not allowed to access this plan, redirecting back')
        navigate('/plans')
      }
    }
  }, [stationId, planStore, navigate])

  useEffect(() => {
    if (stationId) {
      fetchData()
    }
  }, [stationId])

  function getScrollableParent(element: HTMLElement | null): HTMLElement {
    let parent: HTMLElement | null = element?.parentElement || null
    while (parent) {
      const { overflowY } = window.getComputedStyle(parent)
      if (/(auto|scroll|overlay)/.test(overflowY)) return parent
      parent = parent.parentElement
    }
    return (document.scrollingElement as HTMLElement) || document.documentElement
  }

  function getHeaderOffset(): number {
    const H_DEFAULT = window.innerWidth < 768 ? 130 : 100
    const actionHeight = headerRef.current?.getBoundingClientRect().height || 0

    // Detect fixed/sticky top bars currently pinned at the top
    let fixedTop = 0
    const all = Array.from(document.body.querySelectorAll('*')) as HTMLElement[]
    for (const el of all) {
      const style = window.getComputedStyle(el)
      if (style.position === 'fixed' || style.position === 'sticky') {
        const rect = el.getBoundingClientRect()
        const topVal = parseFloat(style.top || '0')
        // Element touching/covering the top area
        if (rect.height > 0 && rect.top <= 0 + 1) {
          fixedTop += rect.height
        } else if (!isNaN(topVal) && topVal === 0 && rect.height > 0) {
          fixedTop += rect.height
        }
      }
    }

    // Prevent absurd values; cap to a reasonable range
    const total = Math.min(Math.max(actionHeight + fixedTop, H_DEFAULT), 360)
    return total
  }

  function handleScrollTo(stepId?: number) {
    if (!stepId) return
    const domId = `station-step-${stepId}`
    const el = document.getElementById(domId) as HTMLElement | null
    if (el) {
      const headerOffset = getHeaderOffset()
      const scrollParent = getScrollableParent(el)
      const parentRect = scrollParent.getBoundingClientRect()
      const elRect = el.getBoundingClientRect()
      const currentScrollTop = scrollParent === (document.scrollingElement as HTMLElement)
        ? window.pageYOffset
        : scrollParent.scrollTop
      const y = currentScrollTop + (elRect.top - parentRect.top) - headerOffset
      if (scrollParent === (document.scrollingElement as HTMLElement)) {
        window.scrollTo({ top: y, behavior: 'smooth' })
      } else {
        scrollParent.scrollTo({ top: y, behavior: 'smooth' as ScrollBehavior })
      }
      if (window.innerWidth < 768) setShowToc(false)
    }
  }

  const summaryItems = useMemo(() => {
    if (!isReviewModeActive) return []
    return [
      { id: 1, stepName: 'Boom Functions Relief Value', changes: ['Instruction detail updated', 'New media added'] },
      { id: 2, stepName: 'Boom Functions Relief Value', changes: ['Media removed'] },
      { id: 3, stepName: 'Boom Functions Relief Value', changes: ['Instruction removed'] },
      { id: 4, stepName: 'Boom Functions Relief Value', changes: ['Instruction detail updated'] },
      { id: 7, stepName: 'Boom Functions Relief Value', changes: ['Detail updated', 'New media added'] },
      { id: 12, stepName: 'Boom Functions Relief Value', changes: ['Detail updated', 'New media added'] }
    ]
  }, [isReviewModeActive])

  const summaryPlanName = 'Removing Red-Tag Status to Open Tech Support SR'
  const summaryTotalChanges = 21

  const reviewOption = planStore?.stationDetail?.reviewSettings?.reviewOption as ReviewOption | undefined
  const requiresSignOff = reviewOption === ReviewOption.SUPERVISOR_SIGNOFF
  const markActionLabel = requiresSignOff ? 'Sign off' : 'Mark as reviewed'

  return (
    <>
        <Box
          position="sticky"
          top="72px"
          zIndex={100}
          width="100%"
          bg="gray.100"
          pb={4}
        >
          {isReviewModeActive && (
            <ReviewBanner
              changeCount={summaryTotalChanges}
              onViewSummary={handleViewSummary}
              onMarkAsReviewed={handleMarkAsReviewed}
              isAllReviewed={isAllReviewed}
              markActionLabel={markActionLabel}
            />
          )}
          <HStack justifyContent="flex-end" marginTop={isReviewModeActive ? '16px' : 0}>
          <Button
              gap={{ base: 0, md: 2 }}
              width={{ base: 10, md: 'initial' }}
              paddingX={{ base: 0, md: 4 }}
              variant="outline"
              background="white"
              border="1px solid #E2E8F0"
              borderRadius="8px"
              color="gray.700"
              fontWeight={500}
              fontSize={{
                base: '0px',
                md: '16px'
              }}
              lineHeight="24px"
              _hover={{ background: 'whiteAlpha.700', borderColor: primaryColor, color: primaryColor }}
              _active={{ background: 'whiteAlpha.700' }}
            >
              <SvgIcon size={16} iconName="outline-message" />
              Help Request
            </Button>
          </HStack>
        </Box>
      <chakra.div ref={headerRef} className={styles.actionWrapper} marginTop={{ base: '75px', md: '75px' }}>
        <VStack spacing={4} width="100%">
          <HStack justifyContent="flex-end" w="100%" flexWrap="wrap" gap={2} spacing={0}>
            
          </HStack>
        </VStack>
      </chakra.div>

      <HStack alignItems="flex-start" width="100%" spacing={4}>
        {/* Left: toggle and compact step list */}
        <Box 
          className={styles.tocSticky} 
          minW={showToc ? '240px' : '44px'} 
          maxW={showToc ? '280px' : '44px'}
          style={{ 
            top: isReviewModeActive ? '200px' : '120px',
            maxHeight: isReviewModeActive ? 'calc(100vh - 200px - 16px)' : 'calc(100vh - 100px - 16px)'
          }}
        >
          <VStack align="stretch" spacing={3}>
            {!showToc ? (
              <IconButton
                aria-label="Open step list"
                icon={<SvgIcon iconName="ic_baseline-format-list-numbered" size={18} />}
                variant="outline"
                size="md"
                background="white"
                border="1px solid #E2E8F0"
                borderRadius="6px"
                onClick={() => setShowToc(true)}
                alignSelf="center"
                width="40px"
                height="40px"
                _hover={{ borderColor: primaryColor }}
              />
            ) : (
              <HStack spacing={2} alignItems="center">
                <IconButton
                  aria-label="Close"
                  size="md"
                  variant="outline"
                  background="white"
                  border="1px solid #E2E8F0"
                  borderRadius="6px"
                  width="40px"
                  height="40px"
                  icon={<SvgIcon iconName="ic_outline-arrow-back-ios" size={18} />}
                  onClick={() => setShowToc(false)}
                  _hover={{ borderColor: primaryColor }}
                />
                <Text fontWeight={600} fontSize="16px" color="gray.700">Table Of Contents</Text>
              </HStack>
            )}
            {showToc && (
              <VStack align="stretch" borderRadius={8}>
                {getValidArray(steps).map((step: IStepWithRelations, index: number) => {
                  const normalizedId = (step?._id)
                  return (
                  <Tooltip
                    key={`toc-${normalizedId}`}
                    label={step?.name || ''}
                    placement="top-start"
                    hasArrow
                    fontSize="sm"
                    color="white"
                    borderRadius="4px"
                    border="none"
                  >
                    <Button
                      type="button"
                      onClick={() => handleScrollTo(normalizedId)}
                      justifyContent="flex-start"
                      variant="unstyled"
                      height="36px"
                      paddingX={1}
                      border="none"
                      _hover={{ color: primaryColor }}
                    >
                      <HStack width="full" onClick={() => handleScrollTo(normalizedId)}>
                        <Text fontWeight={500} color="inherit" minW="20px" textAlign="right">{index + 1}.</Text>
                        <Text noOfLines={1} fontWeight={500} flex={1} textAlign="left" color="inherit" fontSize="14px">{step?.name || 'Untitled'}</Text>
                      </HStack>
                    </Button>
                  </Tooltip>
                )})}
              </VStack>
            )}
          </VStack>
        </Box>

        {/* Right: main steps content */}
        <VStack spacing={3} width="100%">
          {Array.isArray(steps) && steps.length > 0 ? (
            getValidArray(steps)
              .map((step: IStepWithRelations, index: number) => {
                const displayStep = { ...step } as any
                const normalizedId = (step?._id)
                set(displayStep, 'id', normalizedId)
                set(displayStep, 'position', index + 1)
                return (
                  <Col id={`station-step-${normalizedId}`} key={normalizedId} style={{ width: '100%', cursor: 'pointer' }} xl="12" lg="12" md="12" sm="12" xs="12">
                    <StepCard
                      isDragging={false}
                      procedureId={Number(planStore?.stationDetail?.lineBalanceId) || 0}
                      step={displayStep}
                      isCommonStepFromStepId={(displayStep as any)?.originalStepId ? (displayStep as any)?.id : 0}
                      lastStep={steps?.length <= 1}
                      onToggleMessageView={() => { }}
                      searchText={''}
                       isReviewMode={isReviewModeActive}
                      reviewStatusMap={reviewStatusMap}
                      onToggleReviewItem={handleToggleReviewItem}
                    />
                  </Col>
                )
              })
          ) : null}
        </VStack>
      </HStack>
      {isReviewModeActive && (
        <ReviewValidationModal
          isOpen={isReviewValidationModalOpen}
          onClose={() => setIsReviewValidationModalOpen(false)}
        />
      )}
      {isReviewModeActive && (
        <ReviewSummaryModal
          isOpen={isReviewSummaryModalOpen}
          onClose={() => setIsReviewSummaryModalOpen(false)}
          totalChanges={summaryTotalChanges}
          planName={summaryPlanName}
          items={summaryItems}
        />
      )}
      {isReviewModeActive && requiresSignOff && (
        <SignOffConfirmationModal
          isOpen={isSignOffModalOpen}
          onClose={() => setIsSignOffModalOpen(false)}
          onConfirm={handleConfirmSignOff}
        />
      )}
    </>
  )
}

export default observer(PlanStationDetail)

